import { Box, Button, Drawer, Typography, Tabs, Tab, IconButton } from "@mui/material";
import {
  drawerSchema,
  resolveFieldValue,
  sectionHasContent,
} from "./ModalSchema";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { CallDetailDrawer } from "./PromptDrawer";
import { Close } from "@mui/icons-material";
import SupportChat from "./SupportChat";

import { useAuth } from "../../../../../services/auth/AuthContext";
import DocumentsDrawer from "./DocumentsDrawer";

const style = {
  width: "100vw",
  maxWidth: "500px",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  maxHeight: "100vh",
  overflowY: "auto",
  borderRadius: "8px 0 0 8px",
};

const NestedBoxWithSchema = ({ data = {}, schema, depth = 0, root }) => {
  const { t } = useTranslation();

  // The payload root, so a field can read a value from a section other than
  // its own. A replaced question often moved its answer into a different
  // section, and the fallback has to be able to reach the old one.
  const payloadRoot = root ?? data;

  if (!schema || !schema.include) return null;

  const formatValue = (value, fieldSchema) => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return value || value === 0 ? value : fieldSchema.placeholder ?? "Pending";
  };

  const renderField = (key, fieldSchema) => {
    if (!fieldSchema?.include) return null;

    // A field with an explicit source is resolved from the root, so it renders
    // even when its own section is absent - which is exactly the case for a
    // pre-rework enquiry whose answer only exists in the old location.
    const hasSource = Boolean(fieldSchema.source);
    if (!hasSource && !(key in data)) return null;

    if (fieldSchema.type === "nested") {
      const value = data[key];
      const nestedData = value && typeof value === "object" ? value : {};
      // A section with nothing to show is omitted rather than rendered as a
      // heading over an empty box.
      if (!sectionHasContent(payloadRoot, nestedData, fieldSchema)) return null;
      return (
        <div key={key} style={{ marginLeft: "20px", marginTop: "10px" }}>
          <strong>{t(fieldSchema.label || key)}</strong>
          <NestedBoxWithSchema
            data={nestedData}
            schema={fieldSchema}
            depth={depth + 1}
            root={payloadRoot}
          />
        </div>
      );
    }

    if (fieldSchema.type === "list") {
      const rows = Array.isArray(data[key]) ? data[key] : [];
      return (
        <div key={key} style={{ marginLeft: "20px", marginTop: "10px" }}>
          <strong>{t(fieldSchema.label || key)}</strong>
          {rows.length === 0 ? (
            <div style={{ marginTop: "5px" }}>
              {t(fieldSchema.emptyLabel ?? "None")}
            </div>
          ) : (
            rows.map((row, index) => (
              <NestedBoxWithSchema
                key={row?.id ?? index}
                data={row || {}}
                schema={fieldSchema}
                depth={depth + 1}
                root={payloadRoot}
              />
            ))
          )}
        </div>
      );
    }

    const value = resolveFieldValue(payloadRoot, data, key, fieldSchema);

    // A sourced field that resolves to nothing is not applicable to this lead
    // rather than awaiting an answer, so it is omitted instead of showing the
    // "Pending" placeholder. These fields are branch-specific: the salary
    // figure exists only for a salaried applicant and the revenue and profit
    // figures only for a self-employed one, so whichever branch the lead is
    // not on would otherwise read as permanently pending.
    if (hasSource && (value === null || value === undefined || value === "")) {
      return null;
    }

    // A field that only some leads carry at all - a partner's own lead id, for
    // instance - is absent rather than pending on the rest, so it is dropped
    // instead of showing the placeholder.
    if (
      fieldSchema.hideWhenBlank &&
      (value === null || value === undefined || value === "")
    ) {
      return null;
    }

    const displayValue = formatValue(value, fieldSchema);

    return (
      <div key={key} style={{ marginTop: "5px" }}>
        <strong>{t(fieldSchema.label || key)}:</strong> {t(fieldSchema.prefix)} {t(displayValue)}{" "}
        {t(fieldSchema.suffix)}
      </div>
    );
  };

  return (
    <div
      style={{
        padding: depth < 2 ? "10px" : "none",
        border: depth < 2 ? "1px solid #ccc" : "none",
        borderRadius: depth < 2 ? "5px" : "0",
      }}
    >
      {Object.keys(schema.fields || {}).map((key) =>
        renderField(key, schema.fields[key])
      )}
    </div>
  );
};


export const LoanDetailDrawer = ({
  open,
  handleClose,
  initialData,
  priority,
  setPriority,
  showSupportChat,
  addNewChatRecord,
  rejectApplication,
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const [currentTime, setCurrentTime] = useState("");

  const { user } = useAuth();
  const userRoles = user.role;
  const userId = user.id;
  const active_chats = initialData.active_chats;
  const enquiryId = initialData.enquiry_details.id;
  const optimo_phone_id = initialData.enquiry_details.optimo_phone_id;

  // Borrower alongside a working role is not a borrower account: the reduced
  // view applies only when borrower is the whole of it.
  const isBorrower = userRoles.includes('borrower') &&
    !['support', 'partner', 'connector', 'rm', 'bc_partner', 'admin']
      .some((role) => userRoles.includes(role));
  const isSupport = userRoles.includes('support');

  // Once a lead is allocated to Optimo, Optimo takes the actions on it, so
  // everyone except support loses the Actions tab outside the in-progress
  // sections. Support's own view is unchanged in every status.
  const IN_PROGRESS_SUBMISSION_STATUSES = ['pending', 'created_by_support'];
  const isInProgress = IN_PROGRESS_SUBMISSION_STATUSES.includes(
    initialData.enquiry_details.application_submission_status
  );
  const showActionsTab = !isBorrower && (isSupport || isInProgress);

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().toLocaleTimeString();
      setCurrentTime(now);
    }, 1000);

    // Clear interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{ zIndex: 1300 }}
    >
      <Box sx={style}>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          alignItems={'center'}
          mb={2}
        >
          <Typography variant="h5" id="nested-drawer-title">
            {t("details")}
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Tabs value={selectedTab} onChange={handleTabChange} aria-label="Drawer Tabs">
          <Tab label={t("Enquiry Information")} />
          {
            showActionsTab && <Tab label={t("Actions")} />
          }
          {isSupport && showSupportChat && <Tab label={t("Support")} />}

          {isSupport && <Tab label={t("Documents")} />}

        </Tabs>


        {/* Tab Panels */}
        <Box sx={{ mt: 2 }}>
          {/* Calculate actual tab indexes based on which tabs are visible */}
          {(() => {
            let tabIndex = 0;
            let contentIndex = selectedTab;

            // Enquiry Information (always visible)
            if (contentIndex === tabIndex++) {
              return <NestedBoxWithSchema data={initialData} schema={drawerSchema} />;
            }

            // Actions (visible per showActionsTab)
            if (showActionsTab) {
              if (contentIndex === tabIndex++) {
                return (
                  <CallDetailDrawer
                    open={open}
                    handleClose={handleClose}
                    initialData={initialData}
                    priority={priority}
                    setPriority={setPriority}
                    rejectApplication={rejectApplication}
                  />
                );
              }
            }

            // Support (visible if support user and showSupportChat)
            if (isSupport && showSupportChat) {
              if (contentIndex === tabIndex++) {
                return (
                  <SupportChat
                    userId={userId}
                    optimo_phone_id={optimo_phone_id}
                    enquiryId={enquiryId}
                    active_chats={active_chats}
                    addNewChatRecord={addNewChatRecord}
                  />
                );
              }
            }

            // Documents (visible if support user)
            if (isSupport) {
              if (contentIndex === tabIndex++) {
                return (
                  <Box
                    mt={2}
                    textAlign="center"
                    sx={{
                      fontSize: '1.05rem',
                      '& .MuiTableCell-root': { fontSize: '1.05rem' },
                      '& .MuiTableHead-root .MuiTableCell-root': { fontSize: '1.05rem' }
                    }}
                  >
                    <DocumentsDrawer documents={initialData.documents} />
                  </Box>
                );
              }
            }

            return null;
          })()}
        </Box>

        <Box mt={3} textAlign="center">
          <Button variant="contained" onClick={handleClose}>
            {t("close")}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
